'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { Loader2, Calendar, Check, X, CalendarDays, Hourglass, CheckCircle2, XCircle, ListChecks, AlertTriangle, Pencil } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { workingDaysBetween } from '@/lib/date-utils'
import { LEAVE_TYPES } from '@/lib/validators'
import {
  LEAVE_MONTHLY_LIMIT,
  currentMonthKey,
  monthKey,
  formatMonthKey,
  monthlyUsageIndex,
  usageFrom,
  workingDaysInMonth,
  isOverLeaveLimit,
  isPending,
  leaveLimitWarningFor,
} from '@/lib/allowance'
import { AllowanceWarning } from '@/components/ui/allowance-warning'
import { PageShell } from '@/components/ui/page-shell'
import { SectionHeader } from '@/components/ui/section-header'
import { GlassCard } from '@/components/ui/glass-card'
import { KpiTile } from '@/components/ui/kpi-tile'
import { StatusPill, statusTone } from '@/components/ui/status-pill'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { APPROVE_BTN, REJECT_BTN } from '@/components/ui/button-styles'

interface User {
  id: string
  name: string
  employeeId: string | null
  department: string | null
}

interface LeaveRequest {
  id: string
  userId: string | null
  type: string
  startDate: string
  endDate: string
  reason: string
  status: string
  rejectionReason: string | null
  createdAt: string
  user: User
}

const TYPE_TONE: Record<string, Parameters<typeof StatusPill>[0]['tone']> = {
  SICK: 'red',
  CASUAL: 'blue',
  ANNUAL: 'green',
  UNPAID: 'neutral',
  OTHER: 'violet',
}

/**
 * Flags a request whose employee is over — or would be pushed over by this
 * approval — the 2-leave monthly allowance. Renders nothing otherwise.
 */
function OverLimitPill({ leave, allowance }: { leave: LeaveRequest; allowance: { month: string; total: number; pending: boolean; over: boolean } }) {
  if (!allowance.over) return null
  const label = allowance.pending ? 'Would exceed leave limit' : 'Leave limit exceeded'
  return (
    <StatusPill
      tone="red"
      className="mt-1"
      title={`${leaveLimitWarningFor(leave.user?.name)} (${allowance.total} of ${LEAVE_MONTHLY_LIMIT} in ${formatMonthKey(allowance.month)})`}
    >
      <AlertTriangle className="h-3 w-3" />
      {label} · {allowance.total}/{LEAVE_MONTHLY_LIMIT}
    </StatusPill>
  )
}

export function LeaveManagement() {
  const [leaves, setLeaves] = useState<LeaveRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [actionDialog, setActionDialog] = useState<{ open: boolean; leave: LeaveRequest | null; action: 'APPROVED' | 'REJECTED' | null }>({ open: false, leave: null, action: null })
  const [rejectionReason, setRejectionReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [editDialog, setEditDialog] = useState<{ open: boolean; leave: LeaveRequest | null }>({ open: false, leave: null })
  const [editForm, setEditForm] = useState({ type: '', startDate: '', endDate: '', reason: '' })
  const [savingEdit, setSavingEdit] = useState(false)

  const openEditDialog = (leave: LeaveRequest) => {
    setEditForm({
      type: leave.type,
      startDate: leave.startDate,
      endDate: leave.endDate,
      reason: leave.reason || '',
    })
    setEditDialog({ open: true, leave })
  }

  const handleEditSave = async () => {
    if (!editDialog.leave) return
    if (!editForm.type || !editForm.startDate || !editForm.endDate) {
      toast.error('Type, start date and end date are required')
      return
    }
    if (editForm.endDate < editForm.startDate) {
      toast.error('End date must be on or after start date')
      return
    }
    setSavingEdit(true)
    try {
      await apiFetch(`/api/leave/${editDialog.leave.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      })
      toast.success('Leave request updated')
      setEditDialog({ open: false, leave: null })
      fetchData()
      fetchAllLeaves()
    } catch (e: any) {
      toast.error(e.message || 'Failed to update leave request')
    } finally {
      setSavingEdit(false)
    }
  }

  const fetchData = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/leave${filterStatus && filterStatus !== 'all' ? `?status=${filterStatus}` : ''}`)
      const data = await res.json()
      if (data.leaves) setLeaves(data.leaves)
    } catch (error) {
      console.error('Error fetching leaves:', error)
      toast.error('Failed to fetch leave requests')
    } finally {
      setLoading(false)
    }
  }, [filterStatus])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Fetch all leaves for KPI numbers (ignore current filter so KPIs stay consistent).
  // Keyed on mount (not [leaves]) so switching filters doesn't refetch the whole
  // collection; mutations refetch it explicitly via fetchAllLeaves().
  const [allLeaves, setAllLeaves] = useState<LeaveRequest[]>([])
  const fetchAllLeaves = useCallback(() => {
    apiFetch('/api/leave').then(r => r.json()).then(d => {
      if (d.leaves) setAllLeaves(d.leaves)
    }).catch(() => {})
  }, [])
  useEffect(() => {
    fetchAllLeaves()
  }, [fetchAllLeaves])

  const kpis = useMemo(() => {
    const now = new Date()
    const y = now.getFullYear()
    const m = now.getMonth()
    const inMonth = (iso: string) => {
      const d = new Date(iso + 'T00:00:00')
      return d.getFullYear() === y && d.getMonth() === m
    }
    const pending = allLeaves.filter(l => l.status === 'PENDING').length
    const approvedM = allLeaves.filter(l => l.status === 'APPROVED' && inMonth(l.startDate)).length
    const rejected = allLeaves.filter(l => l.status === 'REJECTED').length
    const totalM = allLeaves.filter(l => inMonth(l.startDate)).length
    return { pending, approvedM, rejected, totalM }
  }, [allLeaves])

  // Approved leave days per employee per month, from the unfiltered list — so the
  // allowance is judged against every request, not just the ones on screen.
  const usageIndex = useMemo(
    () => monthlyUsageIndex(allLeaves.map(l => ({ ...l, userId: l.userId ?? l.user?.id ?? null }))),
    [allLeaves]
  )

  /**
   * Where this request leaves the employee's 2-leave month. A pending request
   * counts its own days too — approving it is what would breach the allowance,
   * and leave beyond it may be deducted from salary.
   */
  const allowance = useCallback((leave: LeaveRequest) => {
    const month = monthKey(leave.startDate)
    const userId = leave.userId ?? leave.user?.id ?? null
    const used = usageFrom(usageIndex, userId, month)
    // Only a pending request adds to the projection — approved days are already
    // in `used`, and rejected days were never taken.
    const pending = isPending(leave)
    const total = used + (pending ? workingDaysInMonth(leave.startDate, leave.endDate, month) : 0)
    return { month, total, pending, over: isOverLeaveLimit(total) }
  }, [usageIndex])

  // How many employees are already over their 2 leaves this calendar month.
  const overLimitEmployees = useMemo(() => {
    const suffix = `|${currentMonthKey()}`
    return Object.entries(usageIndex).filter(([key, days]) => key.endsWith(suffix) && isOverLeaveLimit(days)).length
  }, [usageIndex])

  const handleAction = async () => {
    if (!actionDialog.leave || !actionDialog.action) return

    setSubmitting(true)
    try {
      await apiFetch(`/api/leave/${actionDialog.leave.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: actionDialog.action,
          rejectionReason: actionDialog.action === 'REJECTED' ? rejectionReason : undefined
        })
      })

      toast.success(`Leave request ${actionDialog.action.toLowerCase()}`)
      fetchData()
      fetchAllLeaves()
      setActionDialog({ open: false, leave: null, action: null })
      setRejectionReason('')
    } catch (e: any) {
      toast.error(e.message || 'Failed to update leave request')
    } finally {
      setSubmitting(false)
    }
  }

  const getDaysCount = (start: string, end: string) => workingDaysBetween(start, end)

  const fmtDate = (iso: string) => {
    const d = new Date(iso + 'T00:00:00')
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
  }

  return (
    <PageShell>
      <SectionHeader
        icon={CalendarDays}
        title="Leave Requests"
        description="Approve or reject employee leave requests"
        action={
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-[160px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="APPROVED">Approved</SelectItem>
              <SelectItem value="REJECTED">Rejected</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiTile label="Pending" value={kpis.pending} icon={Hourglass} tone="amber" />
        <KpiTile label="Approved (month)" value={kpis.approvedM} icon={CheckCircle2} tone="green" />
        <KpiTile label="Rejected" value={kpis.rejected} icon={XCircle} tone="red" />
        <KpiTile label="Total (month)" value={kpis.totalM} icon={ListChecks} tone="blue" />
        <KpiTile
          label="Over limit"
          value={overLimitEmployees}
          icon={AlertTriangle}
          tone="red"
          hint={`Employees past ${LEAVE_MONTHLY_LIMIT} leaves this month`}
        />
      </div>

      <GlassCard>
        {loading ? (
          <TabLoader />
        ) : leaves.length === 0 ? (
          <EmptyState
            icon={CalendarDays}
            title="No leave requests to display"
            description="Try a different status filter — new requests appear here as employees submit them."
          />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block">
              <ScrollArea className="h-[520px]">
                <div className="min-w-[760px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-neutral-200 dark:border-white/10 hover:bg-transparent">
                        <TableHead className="text-neutral-500 dark:text-white/50">Employee</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Type</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Duration</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Reason</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Status</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leaves.map((leave) => (
                        <TableRow key={leave.id} className="border-neutral-100 dark:border-white/5 hover:bg-neutral-50 dark:bg-white/5">
                          <TableCell>
                            <p className="text-neutral-900 dark:text-white/90 font-medium">{leave.user?.name || 'Unknown User'}</p>
                            <p className="text-xs text-neutral-400 dark:text-white/40">{leave.user?.department || leave.user?.employeeId || '-'}</p>
                            <OverLimitPill leave={leave} allowance={allowance(leave)} />
                          </TableCell>
                          <TableCell>
                            <StatusPill tone={TYPE_TONE[leave.type] || 'neutral'}>{leave.type}</StatusPill>
                          </TableCell>
                          <TableCell>
                            <div className="text-neutral-800 dark:text-white/80">
                              <div className="flex items-center gap-1.5 text-sm">
                                <Calendar className="h-3.5 w-3.5 text-neutral-400 dark:text-white/40" />
                                {fmtDate(leave.startDate)} – {fmtDate(leave.endDate)}
                              </div>
                              <p className="text-xs text-neutral-400 dark:text-white/40 mt-0.5">{getDaysCount(leave.startDate, leave.endDate)} day(s)</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-neutral-700 dark:text-white/70 max-w-[220px]">
                            <p className="truncate">{leave.reason}</p>
                          </TableCell>
                          <TableCell>
                            <StatusPill tone={statusTone(leave.status)} dot>{leave.status}</StatusPill>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <Button
                                size="sm"
                                variant="ghost"
                                aria-label={`Edit leave for ${leave.user?.name || 'employee'}`}
                                className="text-neutral-500 hover:text-orange-600 hover:bg-orange-500/10 dark:text-white/50 dark:hover:text-orange-300"
                                onClick={() => openEditDialog(leave)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              {leave.status === 'PENDING' ? (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-emerald-500/10"
                                    onClick={() => setActionDialog({ open: true, leave, action: 'APPROVED' })}
                                  >
                                    <Check className="h-4 w-4 mr-1" />
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 hover:bg-rose-500/10"
                                    onClick={() => setActionDialog({ open: true, leave, action: 'REJECTED' })}
                                  >
                                    <X className="h-4 w-4 mr-1" />
                                    Reject
                                  </Button>
                                </>
                              ) : (
                                <span className="text-xs text-neutral-400 dark:text-white/40">
                                  {leave.rejectionReason && `Rejected: ${leave.rejectionReason}`}
                                </span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </ScrollArea>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {leaves.map((leave) => (
                <GlassCard key={leave.id} tone={statusTone(leave.status) === 'amber' ? 'amber' : statusTone(leave.status) === 'green' ? 'green' : statusTone(leave.status) === 'red' ? 'red' : 'default'}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-neutral-900 dark:text-white/90 font-medium truncate">{leave.user?.name || 'Unknown'}</p>
                      <p className="text-xs text-neutral-400 dark:text-white/40">{leave.user?.department || leave.user?.employeeId || '-'}</p>
                      <OverLimitPill leave={leave} allowance={allowance(leave)} />
                    </div>
                    <StatusPill tone={statusTone(leave.status)} dot>{leave.status}</StatusPill>
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <StatusPill tone={TYPE_TONE[leave.type] || 'neutral'}>{leave.type}</StatusPill>
                    <span className="text-xs text-neutral-600 dark:text-white/60 inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {fmtDate(leave.startDate)} – {fmtDate(leave.endDate)}
                    </span>
                    <span className="text-xs text-neutral-400 dark:text-white/40">{getDaysCount(leave.startDate, leave.endDate)} day(s)</span>
                  </div>
                  {leave.reason && <p className="mt-2 text-sm text-neutral-700 dark:text-white/70 line-clamp-2">{leave.reason}</p>}
                  {leave.status === 'PENDING' ? (
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" className="flex-1 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/25" onClick={() => setActionDialog({ open: true, leave, action: 'APPROVED' })}>
                        <Check className="h-4 w-4 mr-1" /> Approve
                      </Button>
                      <Button size="sm" className="flex-1 bg-rose-500/15 text-rose-700 dark:text-rose-300 hover:bg-rose-500/25" onClick={() => setActionDialog({ open: true, leave, action: 'REJECTED' })}>
                        <X className="h-4 w-4 mr-1" /> Reject
                      </Button>
                      <Button size="sm" variant="ghost" aria-label="Edit leave" className="text-neutral-500 hover:text-orange-600 hover:bg-orange-500/10 dark:text-white/50 dark:hover:text-orange-300" onClick={() => openEditDialog(leave)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-3 flex items-center gap-2">
                      <Button size="sm" variant="ghost" className="text-neutral-500 hover:text-orange-600 hover:bg-orange-500/10 dark:text-white/50 dark:hover:text-orange-300" onClick={() => openEditDialog(leave)}>
                        <Pencil className="h-4 w-4 mr-1" /> Edit
                      </Button>
                      {leave.rejectionReason && (
                        <p className="text-xs text-neutral-400 dark:text-white/40">Rejected: {leave.rejectionReason}</p>
                      )}
                    </div>
                  )}
                </GlassCard>
              ))}
            </div>
          </>
        )}
      </GlassCard>

      <Dialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ ...actionDialog, open })}>
        <DialogContent className="bg-white/95 dark:bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-neutral-200 dark:border-white/10">
          <DialogHeader>
            <DialogTitle className="text-neutral-900 dark:text-white/90">
              {actionDialog.action === 'APPROVED' ? 'Approve Leave Request' : 'Reject Leave Request'}
            </DialogTitle>
            <DialogDescription className="text-neutral-500 dark:text-white/50">
              {actionDialog.action === 'APPROVED'
                ? 'Are you sure you want to approve this leave request?'
                : 'Please provide a reason for rejecting this leave request.'}
            </DialogDescription>
          </DialogHeader>
          {actionDialog.action === 'APPROVED' && actionDialog.leave && allowance(actionDialog.leave).over && (
            <AllowanceWarning message={leaveLimitWarningFor(actionDialog.leave.user?.name)} />
          )}
          {actionDialog.action === 'REJECTED' && (
            <div className="space-y-2">
              <label className="text-sm text-neutral-700 dark:text-white/70">Rejection Reason</label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection..."
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, leave: null, action: null })} className="border-neutral-200 dark:border-white/10 text-neutral-700 dark:text-white/70">
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={submitting || (actionDialog.action === 'REJECTED' && !rejectionReason)}
              className={actionDialog.action === 'APPROVED' ? APPROVE_BTN : REJECT_BTN}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                actionDialog.action === 'APPROVED' ? 'Approve' : 'Reject'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* HR/Admin leave-details editor — type, dates and reason are all editable
          regardless of status; approved leaves get their balance rebalanced server-side. */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog((d) => ({ ...d, open }))}>
        <DialogContent className="bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-white/10">
          <DialogHeader>
            <DialogTitle className="text-neutral-900 dark:text-white/90">Edit Leave Request</DialogTitle>
            <DialogDescription className="text-neutral-500 dark:text-white/50">
              {editDialog.leave?.user?.name
                ? `Correcting ${editDialog.leave.user.name}'s leave details.`
                : 'Correct the leave details below.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm text-neutral-700 dark:text-white/70">Leave Type</Label>
              <Select value={editForm.type} onValueChange={(v) => setEditForm(f => ({ ...f, type: v }))}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm text-neutral-700 dark:text-white/70">Start Date</Label>
                <Input
                  type="date"
                  value={editForm.startDate}
                  onChange={(e) => setEditForm(f => ({ ...f, startDate: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-neutral-700 dark:text-white/70">End Date</Label>
                <Input
                  type="date"
                  value={editForm.endDate}
                  onChange={(e) => setEditForm(f => ({ ...f, endDate: e.target.value }))}
                />
              </div>
            </div>
            {editForm.startDate && editForm.endDate && editForm.endDate >= editForm.startDate && (
              <p className="text-xs text-neutral-400 dark:text-white/40">
                {workingDaysBetween(editForm.startDate, editForm.endDate)} working day(s)
              </p>
            )}
            <div className="space-y-2">
              <Label className="text-sm text-neutral-700 dark:text-white/70">Reason</Label>
              <Textarea
                value={editForm.reason}
                onChange={(e) => setEditForm(f => ({ ...f, reason: e.target.value }))}
                rows={3}
                placeholder="Reason for the leave…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, leave: null })} className="border-neutral-200 dark:border-white/10 text-neutral-700 dark:text-white/70">
              Cancel
            </Button>
            <Button onClick={handleEditSave} disabled={savingEdit} className="bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white">
              {savingEdit ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  )
}
